/**
 * Purchase-level dedupe shared by the native purchase hooks and PremiumWelcome.
 *
 * The event name is omitted from nativePurchaseDedupeKey on purpose: an INTRO
 * trial is trial_started in the hook and would otherwise be
 * subscription_purchase on the welcome page.
 *
 * The period is whole seconds, Math.floor(epochMs / 1000). The store SDK
 * usually rounds to the second (`2027-10-23T05:00:26Z`) while the server keeps
 * milliseconds (`2027-10-23T05:00:25.600Z` or `2027-10-23T05:00:00.999Z`).
 * The written key is the floored second. A check also looks at the previous
 * and next second, so rounding across a boundary is still one purchase.
 * Two floored seconds apart stay two purchases. The id is the auth user id.
 * profile.id is often missing at purchase time and must not become
 * `anonymous` on one side only.
 *
 * An invalid or missing period returns null and does not throw. PremiumWelcome
 * sends no native fallback on that pass: it cannot tell whether the hook
 * already sent, and a later period end re-runs the effect so a real period
 * still matches the hook's key.
 */

function periodSeconds(periodKey: string | null | undefined): number | null {
  if (periodKey == null || periodKey === '') return null;
  const ms = Date.parse(periodKey);
  if (!Number.isFinite(ms)) return null;
  return Math.floor(ms / 1000);
}

function purchaseKeyForSecond(
  userId: string | null | undefined,
  billingTier: string,
  second: number,
): string {
  return `jp:checkout_conversion:${userToken(userId)}:${billingTier}:${second}`;
}

function userToken(userId: string | null | undefined): string {
  return userId || 'anonymous';
}

export function nativePurchaseDedupeKey(args: {
  userId: string | null | undefined;
  billingTier: string;
  periodKey: string | null | undefined;
}): string | null {
  const second = periodSeconds(args.periodKey);
  if (second == null) return null;
  return purchaseKeyForSecond(args.userId, args.billingTier, second);
}

/**
 * The floored second plus its two neighbours. Callers write only the floored
 * key and treat a hit on any of these three as the same purchase.
 */
export function nativePurchaseDedupeCandidates(args: {
  userId: string | null | undefined;
  billingTier: string;
  periodKey: string | null | undefined;
}): string[] | null {
  const second = periodSeconds(args.periodKey);
  if (second == null) return null;
  return [second - 1, second, second + 1].map((neighbour) =>
    purchaseKeyForSecond(args.userId, args.billingTier, neighbour),
  );
}

/** Older name-based checkout key. Same user id and period token as the purchase key. */
export function nativeNamedCheckoutDedupeKey(args: {
  userId: string | null | undefined;
  eventName: string;
  billingTier: string;
  periodKey: string | null | undefined;
}): string | null {
  const second = periodSeconds(args.periodKey);
  if (second == null) return null;
  return `jp:checkout_conversion:${userToken(args.userId)}:${args.eventName}:${args.billingTier}:${second}`;
}
